import { useFeatureVisibility } from '@nextgen-web-framework/all';
import { useMyAccountProfileConfig } from './masterdata';
import { getConfigByPK, MasterConfigNameConst, getExt, getInputConfig } from './myAccountProfileUtils';
import { MyAccountExtEnum } from './types/enum';

export const useFeatureFlag = () => {
  // Temporary add this logic to avoid breaking changes,
  // to fallback to masterdata if feature visibility is not available
  // To avoid break in QA, this setting is only available for QA

  const getBoolean = (val: unknown): boolean => (typeof val === 'boolean' ? val : false);

  // Co-App - fallback to masterdata
  const myAccountConfig = useMyAccountProfileConfig();
  const { ext: coAppConfig } = getConfigByPK(myAccountConfig, MasterConfigNameConst.STEP_CO_APP_FORM);
  const coAppFallback = Boolean(getExt(MyAccountExtEnum.CO_APP_ENABLE_ADD, coAppConfig)) ?? false;
  const enableCoAppAdd = useFeatureVisibility?.('profile.coapp.enableAdd') || coAppFallback;

  // Life Cycle
  const { inputs: lifeCycleConfig } = getConfigByPK(myAccountConfig, MasterConfigNameConst.STEP_ACCOUNT_LIFE_CYCLE);

  // Renewal
  const renewalFallback = getBoolean(getInputConfig(lifeCycleConfig, 'renewal')?.visible);
  const enableRenewal = useFeatureVisibility?.('profile.lifecycle.enableRenewal') || renewalFallback;

  // Upgrade - fallback to masterdata
  const upgradeFallback = getBoolean(getInputConfig(lifeCycleConfig, 'allowUpgrade')?.visible);
  const enableUpgrade = useFeatureVisibility?.('profile.lifecycle.enableUpgrade') || upgradeFallback;

  // Downgrade - fallback to masterdata
  const enableDowngrade = useFeatureVisibility?.('profile.lifecycle.enableDowngrade') || false;

  // Auto Renewal - fallback to masterdata
  const autoRenewalConfig = getInputConfig(lifeCycleConfig, 'allowAutoRenewal');
  const autoRenewalFallback = getBoolean(autoRenewalConfig?.visible);
  const autoRenewalCheckFallback = getBoolean(autoRenewalConfig?.checkAutoRenewStatus);
  const allowRenewOnRenewalMonthFallback = getBoolean(autoRenewalConfig?.allowRenewOnRenewalMonth);
  const allowUpdateOnRenewalMonthFallback = getBoolean(autoRenewalConfig?.allowUpdateOnRenewalMonth);

  const enableAutoRenewal = useFeatureVisibility?.('profile.lifecycle.enableAutoRenewal') || autoRenewalFallback;
  const enableAutoRenewalCheck =
    useFeatureVisibility?.('profile.lifecycle.enableAutoRenewalCheck') || autoRenewalCheckFallback;
  const enableAllowRenewOnAutoRenewalMonth =
    useFeatureVisibility?.('profile.lifecycle.allowRenewOnAutoRenewalMonth') || allowRenewOnRenewalMonthFallback;
  const enableAllowUpdateOnAutoRenewalMonth =
    useFeatureVisibility?.('profile.lifecycle.allowUpdateOnAutoRenewalMonth') || allowUpdateOnRenewalMonthFallback;

  // Disable manual renewal for diamond and above
  const disableManualRenewalForDiamond =
    useFeatureVisibility?.('profile.lifecycle.disableManualRenewalForDiamond') ?? false;
  const disableDowngradeForPlatinum = useFeatureVisibility?.('profile.lifecycle.disableDowngradeForPlatinum') ?? false;

  return {
    enableCoAppAdd,
    enableRenewal,
    enableUpgrade,
    enableDowngrade,
    enableAutoRenewal,
    enableAutoRenewalCheck,
    enableAllowRenewOnAutoRenewalMonth,
    enableAllowUpdateOnAutoRenewalMonth,
    disableManualRenewalForDiamond,
    disableDowngradeForPlatinum,
  };
};
